import { Home, type LucideIcon } from "lucide-react";
import { ScopedLink } from "@/lib/scoped-nav";
import { useRole } from "@/lib/roles";
import { PERSPECTIVES, actionNav } from "@/lib/perspectives";
import { adminNav } from "@/lib/role-content";
import { KairosLogo } from "./KairosLogo";

function NavLink({
  to,
  label,
  Icon,
}: {
  to: string;
  label: string;
  Icon: LucideIcon;
}) {
  return (
    <ScopedLink
      to={to as "/"}
      activeOptions={{ exact: to === "/" }}
      className="group flex items-center gap-2.5 rounded-md pr-3 pl-[10px] py-1.5 text-sm border-l-2 transition-colors"
      activeProps={{
        className:
          "border-sidebar-primary bg-sidebar-primary/15 text-sidebar-foreground font-medium",
      }}
      inactiveProps={{
        className:
          "border-transparent text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
      }}
    >
      <Icon className="h-4 w-4 shrink-0 transition-colors text-sidebar-foreground/40 group-hover:text-sidebar-primary group-aria-[current=page]:text-sidebar-primary" />
      <span>{label}</span>
    </ScopedLink>
  );
}

function NavGroup({ label }: { label: string }) {
  return (
    <p className="px-3 pb-1 pt-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70 select-none">
      {label}
    </p>
  );
}

export function PerspectiveSidebar() {
  const { role } = useRole();
  const actions = actionNav(role);
  // Konfiguraatio nav row is hidden for non-configuration roles (story #833) — a
  // nav-visibility exception on top of, not instead of, its structural/write gate.
  const perspectives = PERSPECTIVES.filter(
    (p) => p.id !== "configuration" || role === "configuration",
  );

  return (
    <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-border bg-sidebar">
      <ScopedLink
        to="/"
        activeOptions={{ exact: true }}
        className="px-5 py-5 flex items-center gap-2 border-b border-border transition-colors hover:bg-sidebar-accent"
      >
        <KairosLogo size={32} />
        <div className="leading-tight">
          <div className="text-sm font-semibold tracking-tight">BKRY</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Kairos · Digitaalinen kiinteistöjohtaja
          </div>
        </div>
      </ScopedLink>

      <nav className="px-2 pt-4">
        <div className="space-y-0.5">
          <NavLink to="/" label="Etusivu" Icon={Home} />
        </div>

        <div className="mt-4">
          <NavGroup label="Perspektiivi" />
          <div className="space-y-0.5">
            {perspectives.map((p) => (
              <NavLink key={p.to} to={p.to} label={p.label} Icon={p.icon} />
            ))}
          </div>
        </div>

        <div className="mt-4">
          <NavGroup label="Toiminnot" />
          <div className="space-y-0.5">
            {actions.map((a) => (
              <NavLink key={a.to} to={a.to} label={a.label} Icon={a.icon} />
            ))}
          </div>
        </div>

        <div className="mt-4">
          <NavGroup label="Info" />
          <div className="space-y-0.5">
            {adminNav.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} label={label} Icon={Icon} />
            ))}
          </div>
        </div>
      </nav>
    </aside>
  );
}
